import { LayerName } from "../../config/layers";
import BaseEntity from "../../core/entity/BaseEntity";
import { V, V2d } from "../../core/Vector";
import VectorSprite from "../graphics/VectorSprite";
import { createCharacterSegments } from "../graphics/VectorSpriteUtils";
import vectorFont, { VectorFont } from "./VectorFont";

export enum TextAnchor {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export enum VerticalAnchor {
  TOP = "top",
  CENTER = "center",
  BOTTOM = "bottom",
}

export interface VectorTextOptions {
  text: string;
  position?: V2d;
  scale?: number;
  color?: number;
  strokeWidth?: number;
  anchor?: TextAnchor;
  verticalAnchor?: VerticalAnchor;
  layer?: LayerName;
}

export class VectorText extends BaseEntity {
  pausable = false; // Text should render even when game is paused
  private renderer: VectorSprite;

  private _text: string;
  private _scale: number;
  private _color: number;
  private _strokeWidth: number;
  private _anchor: TextAnchor;
  private _verticalAnchor: VerticalAnchor;
  private font: VectorFont;
  private needsRedraw: boolean = true;

  constructor(options: VectorTextOptions) {
    super();
    this._text = options.text;
    this._scale = options.scale ?? 1;
    this._color = options.color ?? 0xffffff;
    this._strokeWidth = options.strokeWidth ?? 1;
    this._anchor = options.anchor ?? TextAnchor.LEFT;
    this._verticalAnchor = options.verticalAnchor ?? VerticalAnchor.TOP;
    this.font = vectorFont;

    // Create renderer with initial empty segments
    this.renderer = new VectorSprite([], options.layer);

    if (options.position) {
      this.renderer.setPosition(options.position);
    }
  }

  get text(): string {
    return this._text;
  }

  set text(value: string) {
    if (this._text !== value) {
      this._text = value;
      this.needsRedraw = true;
    }
  }

  get scale(): number {
    return this._scale;
  }

  set scale(value: number) {
    if (this._scale !== value) {
      this._scale = value;
      this.needsRedraw = true;
    }
  }

  get color(): number {
    return this._color;
  }

  set color(value: number) {
    if (this._color !== value) {
      this._color = value;
      this.needsRedraw = true;
    }
  }

  get strokeWidth(): number {
    return this._strokeWidth;
  }

  set strokeWidth(value: number) {
    if (this._strokeWidth !== value) {
      this._strokeWidth = value;
      this.needsRedraw = true;
    }
  }

  get anchor(): TextAnchor {
    return this._anchor;
  }

  set anchor(value: TextAnchor) {
    if (this._anchor !== value) {
      this._anchor = value;
      this.needsRedraw = true;
    }
  }

  get verticalAnchor(): VerticalAnchor {
    return this._verticalAnchor;
  }

  set verticalAnchor(value: VerticalAnchor) {
    if (this._verticalAnchor !== value) {
      this._verticalAnchor = value;
      this.needsRedraw = true;
    }
  }

  get position(): V2d {
    return this.renderer.getPosition();
  }

  set position(value: V2d) {
    this.renderer.setPosition(value);
  }

  get textSprite() {
    return this.renderer.sprite;
  }

  onAdd(): void {
    // Add the renderer as a child entity
    this.addChild(this.renderer);
    this.redrawText();
  }

  onRender(): void {
    if (this.needsRedraw) {
      this.redrawText();
    }
  }

  private redrawText(): void {
    if (!this._text) {
      this.renderer.setSegments([]);
      this.needsRedraw = false;
      return;
    }

    // Calculate the total width and height of the text for anchor positioning
    const textWidth = this.calculateTextWidth();
    const textHeight = this.calculateTextHeight();

    // Determine starting X offset based on horizontal anchor
    let startXOffset = 0;
    switch (this._anchor) {
      case TextAnchor.LEFT:
        startXOffset = 0;
        break;
      case TextAnchor.CENTER:
        startXOffset = -textWidth / 2;
        break;
      case TextAnchor.RIGHT:
        startXOffset = -textWidth;
        break;
    }

    // Determine Y offset based on vertical anchor
    let yOffset = 0;
    switch (this._verticalAnchor) {
      case VerticalAnchor.TOP:
        yOffset = 0;
        break;
      case VerticalAnchor.CENTER:
        yOffset = -textHeight / 2;
        break;
      case VerticalAnchor.BOTTOM:
        yOffset = -textHeight;
        break;
    }

    // Collect all segments for the text
    const allSegments = [];
    let xOffset = startXOffset;

    for (let i = 0; i < this._text.length; i++) {
      const char = this._text[i].toUpperCase();

      if (char === " ") {
        xOffset += 3 * this._scale;
        continue;
      }

      const character = this.font.characters.get(char);
      if (!character) {
        xOffset += 3 * this._scale;
        continue;
      }

      // Create segments for this character
      const characterSegments = createCharacterSegments(
        character.segments,
        this._color,
        this._strokeWidth,
        this._scale,
        V(xOffset, yOffset),
      );

      allSegments.push(...characterSegments);
      xOffset += (character.width + this.font.spacing) * this._scale;
    }

    // Update the renderer with all segments
    this.renderer.setSegments(allSegments);
    this.needsRedraw = false;
  }

  calculateTextWidth(): number {
    if (!this._text) {
      return 0;
    }

    let width = 0;

    for (let i = 0; i < this._text.length; i++) {
      const char = this._text[i].toUpperCase();

      if (char === " ") {
        width += 3 * this._scale;
        continue;
      }

      const character = this.font.characters.get(char);
      if (!character) {
        width += 3 * this._scale;
        continue;
      }

      width += (character.width + this.font.spacing) * this._scale;
    }

    return width - this.font.spacing * this._scale;
  }

  calculateTextHeight(): number {
    return this.font.height * this._scale;
  }
}
